import { Checkbox, Col, Row } from 'antd';
import mapGuide from '../../../../assets/images/mapkey.png';
import GoogleMapReact from 'google-map-react';
import MapPopover from './MapPopover';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import useSupercluster from 'use-supercluster';
import ResetSearchButton from './ResetSearchButton';

const Marker = ({ children }) => children;

const ResultsMap = forwardRef(
  (
    {
      bounds,
      setBounds,
      usingBounds,
      setUsingBounds,
      bookableOnly = false,
      results,
      showReset = false,
      isInitialResults = false,
      onBoundsChange = () => {},
      onBoundsEnableChange = (enabled) => {},
      resetSearchAndFilters = null,
    },
    ref
  ) => {
    const mapRef = useRef(null);
    const mapsApiRef = useRef(null);
    const [zoom, setZoom] = useState(8);

    // Map Points
    const points = useMemo(
      () =>
        (results || []).map((fishery) => ({
          ...fishery,
          type: 'Feature',
          thumbnail: fishery.primary_image_url,
          id: fishery.public_id,
          private_id: fishery.id,
          rating: fishery.rating,
          favourite: fishery.is_favourite,
          properties: {
            cluster: false,
            fisheryId: fishery.public_id,
          },
          geometry: {
            type: 'Point',
            coordinates: [
              fishery.coordinates ? fishery.coordinates.lng : -1.8890204,
              fishery.coordinates ? fishery.coordinates.lat : 53.6238296,
            ],
          },
        })),
      [results]
    );

    const CLUSTER_LIMIT = 25;
    const MAX_ZOOM = 15;

    // Map cluster objects
    const { clusters: directoryClusters, supercluster } = useSupercluster({
      points: (points ?? []).filter((p) => !p?.is_bookable),
      bounds,
      zoom,
      options: {
        radius: 75,
        maxZoom: CLUSTER_LIMIT,
      },
    });
    const clusters = useMemo(() => {
      const bookableClusters = points.filter((p) => p?.is_bookable);
      return directoryClusters.concat(bookableClusters);
    }, [directoryClusters, points]);

    const prevUsingBounds = useRef(usingBounds);
    const prevBookableOnly = useRef(bookableOnly);
    const autoFitBounds = useCallback(
      (fisheries, ukOnly) => {
        const oldPrevUsingBounds = prevUsingBounds.current;
        const oldPrevBookableOnly = prevBookableOnly.current;
        prevUsingBounds.current = usingBounds;
        prevBookableOnly.current = bookableOnly;
        if (
          oldPrevUsingBounds !== usingBounds ||
          oldPrevBookableOnly !== bookableOnly
        ) {
          // Prevents changing zoom/position when changing usingBounds or bookableOnly
          return;
        }
        if (
          !fisheries?.length ||
          usingBounds ||
          !mapRef.current ||
          !mapsApiRef.current
        ) {
          return;
        }
        const ukOnlyCountries = [
          'England',
          'Wales',
          'Scotland',
          'Northern Ireland',
          'Ireland',
        ];
        const bounds = new mapsApiRef.current.LatLngBounds();

        fisheries
          .filter((fishery) =>
            ukOnly && fishery?.country
              ? ukOnlyCountries.includes(fishery.country)
              : true
          )
          .filter(
            (fishery) =>
              // Don't auto-fit on fisheries with null/zero lat/lon
              fishery.location.lat && fishery.location.lon
          )
          .map((fishery) => fishery.location)
          .forEach((location) => {
            bounds.extend(
              new mapsApiRef.current.LatLng(location.lat, location.lon)
            );
          });
        mapRef.current.fitBounds(bounds);
        mapRef.current.setZoom(Math.min(mapRef.current.getZoom(), 10));
      },
      [bookableOnly, usingBounds]
    );

    const handleApiLoaded = (map, maps) => {
      mapRef.current = map;
      mapsApiRef.current = maps;
    };

    useImperativeHandle(
      ref,
      () => ({
        autoFit: () => autoFitBounds(results, isInitialResults),
        handleReset: () => {
          prevUsingBounds.current = false;
        },
      }),
      [autoFitBounds, isInitialResults, results]
    );

    const handleZoomBoundsChange = (zoom, bounds) => {
      setZoom(zoom);
      setBounds([bounds.nw.lng, bounds.se.lat, bounds.se.lng, bounds.nw.lat]);
      if (usingBounds) {
        onBoundsChange(bounds);
      }
    };

    const handleSearchAsIMoveChange = () => {
      setUsingBounds((prev) => {
        const newValue = !prev;
        onBoundsEnableChange(newValue);
        return newValue;
      });
    };

    return (
      <Row id='sb-map' ref={ref}>
        <Col span={24}>
          <div className='google-search-map-swimbooker'>
            {/* <span id='search-as-i-move' onClick={handleSearchAsIMoveChange}>
              <Checkbox checked={usingBounds} />
              <span className='gSemiBold fs-14 txt-nowrap'>
                Search as I move the map
              </span>
            </span>
            {resetSearchAndFilters && showReset && (
              <span id='map-reset-btn'>
                <ResetSearchButton reset={resetSearchAndFilters} />
              </span>
            )} */}
            {/* <span id='map-icon-guide'>
              <img
                style={{ height: '100%' }}
                alt='Legend for map markings'
                src={mapGuide}
              />
            </span> */}
            <GoogleMapReact
              bootstrapURLKeys={{
                key: process.env.REACT_APP_GOOGLE_API_KEY,
              }}
              defaultCenter={{ lat: 51.5074, lng: 0.1278 }}
              defaultZoom={zoom}
              zoom={zoom}
              options={{
                gestureHandling: 'greedy',
                disableDefaultUI: true,
                minZoom: 3,
                maxZoom: MAX_ZOOM,
              }}
              yesIWantToUseGoogleMapApiInternals
              onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
              onChange={({ zoom, bounds }) =>
                handleZoomBoundsChange(zoom, bounds)
              }
              ref={mapRef}
            >
              {results?.length
                ? clusters.map((cluster) => {
                    const [longitude, latitude] = cluster.geometry.coordinates;
                    const { cluster: isCluster, point_count: pointCount } =
                      cluster.properties;

                    if (isCluster) {
                      if (zoom < MAX_ZOOM) {
                        const clusterFisheries = supercluster.getLeaves(
                          cluster.id
                        );
                        return (
                          <Marker
                            key={clusterFisheries.reduce(
                              // Ensures that clusters have distinct keys from fisheries in case of duplicated cluster IDs
                              (acc, c) => acc + (c?.id ?? '').toString(),
                              ''
                            )}
                            lat={latitude}
                            lng={longitude}
                          >
                            <div
                              className='cluster-marker grey-100 grey-bg-400'
                              onClick={() => {
                                const expansionZoom = Math.min(
                                  supercluster.getClusterExpansionZoom(
                                    cluster.id
                                  ),
                                  MAX_ZOOM
                                );
                                mapRef.current.setZoom(expansionZoom);
                                mapRef.current.panTo({
                                  lat: latitude,
                                  lng: longitude,
                                });
                              }}
                            >
                              {pointCount}
                            </div>
                          </Marker>
                        );
                      } else {
                        // If locations are too close to be separate at full zoom, then display them in an offset group
                        const clusterFisheries = supercluster.getLeaves(
                          cluster.id
                        );
                        return clusterFisheries.map((cluster, i) => {
                          // Distribute points on concentric circles
                          const scaleFactor = 25;
                          const circleNumber = Math.ceil(i / 5);
                          const angle = (((2 * Math.PI) / 5) * i) % 5;
                          const angleOffset =
                            (((2 * Math.PI) / 10) * circleNumber) % 2;
                          const xOffsetPx =
                            circleNumber *
                            scaleFactor *
                            Math.sin(angle + angleOffset);
                          const yOffsetPx =
                            circleNumber *
                            scaleFactor *
                            Math.cos(angle + angleOffset);
                          return (
                            <Marker
                              key={cluster.properties.fisheryId}
                              lng={longitude}
                              lat={latitude}
                              style={{
                                translate: `${xOffsetPx}px ${yOffsetPx}px`,
                              }}
                            >
                              <div
                                style={{
                                  translate: `${xOffsetPx}px ${yOffsetPx}px`,
                                }}
                              >
                                <MapPopover data={cluster} />
                              </div>
                            </Marker>
                          );
                        });
                      }
                    }
                    return (
                      <Marker
                        key={cluster.properties.fisheryId}
                        lat={latitude}
                        lng={longitude}
                      >
                        <MapPopover data={cluster} />
                      </Marker>
                    );
                  })
                : ''}
            </GoogleMapReact>
          </div>
        </Col>
      </Row>
    );
  }
);

export default ResultsMap;
